import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { graphql } from "gatsby";
import { MDXRenderer, MDXRendererProps } from "gatsby-plugin-mdx";
import { MDXProvider } from "@mdx-js/react";
import {
  Alert,
  AlertTitle,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { TableOfContentItem, TableOfContents } from "../components/toc";
import { Link } from "../utils";

type DocumentationTemplateProps = {
  data: {
    mdx: {
      body: string;
      tableOfContents: { items: TableOfContentItem[] };
    };
    site: {
      siteMetadata: {
        title: string;
        repository: string;
      };
    };
  };
  pageContext: { slug: string };
};

const styles = {
  my3: {
    margin: (theme: Theme) => theme.spacing(3, 0),
  },
};

function MdxRender({ children, ...rest }: MDXRendererProps) {
  const components = {
    h1: (_: any) => null,
    a: ({ href, ...rest }: any) => <Link to={href} {...rest} />,
    table: (props: any) => (
      <TableContainer component={Paper}>
        <Table size="small" {...props} />
      </TableContainer>
    ),
    thead: (props: any) => <TableHead {...props} />,
    tbody: (props: any) => <TableBody {...props} />,
    tr: (props: any) => <TableRow {...props} />,
    th: (props: any) => <TableCell size="medium" {...props} />,
    td: (props: any) => <TableCell {...props} />,
  };
  return (
    <MDXProvider components={components} {...rest}>
      <MDXRenderer>{children}</MDXRenderer>
    </MDXProvider>
  );
}

export default function DocumentationTemplate(
  props: DocumentationTemplateProps
) {
  const {
    data: {
      mdx: post,
      site: {
        siteMetadata: { title: siteTitle, repository },
      },
    },
    pageContext: { slug },
  } = props;
  const pageTitle = useMemo(() => post.tableOfContents.items[0]?.title, [post]);

  return (
    <>
      <Helmet>
        <title>{`${pageTitle} - ${siteTitle}`}</title>
      </Helmet>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8} lg={9}>
          <Typography component="h1" variant="h4">
            {pageTitle}
          </Typography>
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <TableOfContents toc={post.tableOfContents.items} />
          </Box>
          <Box sx={styles.my3}>
            <MdxRender>{post.body}</MdxRender>
          </Box>
          <Box sx={styles.my3}>
            <Alert severity="info">
              <AlertTitle>反馈</AlertTitle>
              <Typography variant="body2">
                <Link to={`${repository}/-/issues`}>
                  提交和查看关于此文档的反馈
                </Link>
                或者
                <Link to={`${repository}/-/blob/main/docs/${slug}.mdx`}>
                  编辑这篇文档
                </Link>
                。
              </Typography>
            </Alert>
          </Box>
        </Grid>
        <Box component={Grid} item md={4} lg={3} sx={{ display: { xs: "none", md: "block" } }}>
          <TableOfContents toc={post.tableOfContents.items} />
        </Box>
      </Grid>
    </>
  );
}

export const pageQuery = graphql`
  query ($slug: String!) {
    mdx(slug: { eq: $slug }) {
      id
      excerpt(pruneLength: 160)
      body
      tableOfContents(maxDepth: 2)
    }
    site {
      siteMetadata {
        title
        repository
      }
    }
  }
`;
